import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/ui-custom/Reveal";
import { Seo, breadcrumbSchema } from "@/components/seo/Seo";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { BrandCard } from "@/components/brands/BrandCard";
import { CTABand } from "@/components/home/CTABand";
import { SERVICES } from "@/content/services";
import NotFound from "@/pages/NotFound";

export default function CategoryPage() {
  const { slug } = useParams();
  const [cat, setCat] = useState(null);
  const [cats, setCats] = useState([]);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setCat(null); setMissing(false);
    api.get(`/categories/${slug}`).then((r) => setCat(r.data)).catch(() => setMissing(true));
    api.get("/categories").then((r) => setCats(r.data));
  }, [slug]);

  if (missing) return <NotFound />;
  if (!cat) return <div className="min-h-screen" />;

  const categoryMap = Object.fromEntries(cats.map((c) => [c.slug, c.name]));
  const crumbs = [{ to: "/brands", label: "Range" }, { to: `/range/${slug}`, label: cat.name }];
  const service = cat.related_service && SERVICES[cat.related_service];

  return (
    <main data-testid="category-page">
      <Seo title={cat.seo_title || `Wholesale ${cat.name} Supplier Sydney`} description={cat.meta_description || cat.description?.slice(0, 160)} image={cat.brands[0]?.hero_image_url} schema={breadcrumbSchema(crumbs)} />
      <section className="pt-32 sm:pt-40 pb-12 mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <Breadcrumbs items={crumbs} />
        <div className="grid lg:grid-cols-12 gap-10 mt-10">
          <div className="lg:col-span-7">
            <Reveal><p className="eyebrow text-[var(--terracotta)] mb-5">Wholesale range</p></Reveal>
            <Reveal delay={0.05}><h1 className="font-display font-normal text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-[var(--ink)]" data-testid="category-h1">{cat.name}</h1></Reveal>
            <Reveal delay={0.15}><p className="mt-8 text-base md:text-lg text-[var(--charcoal)] font-light leading-relaxed max-w-3xl" data-testid="category-intro">{cat.description || `Deliver supplies ${cat.name.toLowerCase()} wholesale to independent supermarkets, grocers, delis, cafes and restaurants across Sydney and NSW. Explore the ranges below and download the latest catalogues.`}</p></Reveal>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 flex flex-col justify-end gap-4">
            <Reveal delay={0.2}>
              <Link to={`/become-a-stockist?interest=${slug}`} className="btn-pill btn-ink w-full justify-center" data-testid="category-enquire-cta">Enquire about {cat.name.toLowerCase()} <ArrowUpRight size={14} /></Link>
            </Reveal>
            {service && (
              <Reveal delay={0.25}>
                <Link to={service.path} className="block border border-[var(--sand)] rounded-sm p-5 hover:border-[var(--ink)] transition-colors" data-testid="category-related-service">
                  <p className="eyebrow text-[var(--muted)]">Related service</p>
                  <p className="font-display text-lg mt-1 text-[var(--ink)]">{service.navLabel} <ArrowRight size={14} className="inline ml-1" /></p>
                </Link>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 pb-20">
        <div className="flex items-baseline justify-between border-b border-[var(--sand)] pb-4 mb-8">
          <h2 className="font-display font-normal text-2xl text-[var(--ink)]">{cat.name} ranges</h2>
          <p className="text-sm text-[var(--muted)]" data-testid="category-count">{cat.brands.length} {cat.brands.length === 1 ? "range" : "ranges"}</p>
        </div>
        {cat.brands.length === 0 ? (
          <p className="text-[var(--charcoal)] font-light">New ranges in this category are coming soon. <Link to="/contact" className="link-underline">Contact our team</Link> for current availability.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6" data-testid="category-brands-grid">
            {cat.brands.map((b, i) => <BrandCard key={b.id} brand={b} categoryMap={categoryMap} index={i} />)}
          </div>
        )}
      </section>

      <section className="bg-[var(--cream-2)] py-16">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
          <p className="eyebrow text-[var(--muted)] mb-5">Other wholesale categories</p>
          <div className="flex flex-wrap gap-2" data-testid="category-other-list">
            {cats.filter((c) => c.slug !== slug && c.brand_count > 0).map((c) => (
              <Link key={c.slug} to={`/range/${c.slug}`} className="chip !text-sm">{c.name}</Link>
            ))}
          </div>
        </div>
      </section>
      <CTABand />
    </main>
  );
}
